import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Users, Calendar, Phone, 
  CheckCircle, PhoneMissed, Clock, Loader2, BarChart3 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDateEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function ClientPerformance() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30); // days

  useEffect(() => {
    fetchPerformanceData();
  }, [timeRange]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/client/performance/summary?days=${timeRange}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!summary || !summary.leads_timeline) return [];
    
    // Group by date and aggregate
    const dataMap = {};
    summary.leads_timeline.forEach(record => {
      const date = record.date;
      if (!dataMap[date]) {
        dataMap[date] = {
          date,
          leads: 0,
          qualified: 0,
          appointments: 0
        };
      }
      dataMap[date].leads += record.total_leads;
      dataMap[date].qualified += record.qualified_leads;
      dataMap[date].appointments += record.booked_appointments;
    });

    return Object.values(dataMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const prepareSourceData = () => {
    if (!summary || !summary.leads_by_source) return [];
    
    return Object.entries(summary.leads_by_source).map(([name, value]) => ({
      name,
      value
    }));
  };

  const formatNumber = (num) => {
    return num || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const chartData = prepareChartData();
  const sourceData = prepareSourceData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-black/20 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/client-dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-white">Performance Analytics</h1>
              <p className="text-blue-200 text-sm">Track your marketing results and growth</p>
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Leads */}
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all">
            <div className="flex items-center justify-between mb-3">
              <Users className="h-8 w-8 text-blue-400" />
              <TrendingUp className="h-4 w-4 text-green-400" />
            </div>
            <p className="text-blue-100 text-sm font-medium mb-1">Total Leads</p>
            <p className="text-3xl font-bold text-white">{formatNumber(summary?.total_leads)}</p>
          </div>

          {/* Qualified Leads */}
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all">
            <div className="flex items-center justify-between mb-3">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <p className="text-blue-100 text-sm font-medium mb-1">Qualified Leads</p>
            <p className="text-3xl font-bold text-white">{formatNumber(summary?.qualified_leads)}</p>
            {summary?.total_leads > 0 && (
              <p className="text-xs text-blue-300 mt-1">
                {Math.round((summary.qualified_leads / summary.total_leads) * 100)}% qualification rate
              </p>
            )}
          </div>

          {/* Booked Appointments */}
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all">
            <div className="flex items-center justify-between mb-3">
              <Calendar className="h-8 w-8 text-purple-400" />
            </div>
            <p className="text-blue-100 text-sm font-medium mb-1">Appointments</p>
            <p className="text-3xl font-bold text-white">{formatNumber(summary?.booked_appointments)}</p>
            {summary?.qualified_leads > 0 && (
              <p className="text-xs text-blue-300 mt-1">
                {Math.round((summary.booked_appointments / summary.qualified_leads) * 100)}% booking rate
              </p>
            )}
          </div>

          {/* Total Calls */}
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-all">
            <div className="flex items-center justify-between mb-3">
              <Phone className="h-8 w-8 text-yellow-400" />
            </div>
            <p className="text-blue-100 text-sm font-medium mb-1">Total Calls</p>
            <p className="text-3xl font-bold text-white">{formatNumber(summary?.total_calls)}</p>
            {summary?.total_calls > 0 && (
              <p className="text-xs text-blue-300 mt-1">
                {Math.round((summary.answered_calls / summary.total_calls) * 100)}% answered
              </p>
            )}
          </div>
        </div>

        {/* Leads Trend Chart */}
        {chartData.length > 0 && (
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">Leads Over Time</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="rgba(255,255,255,0.5)"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="rgba(255,255,255,0.5)" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2} name="Total Leads" />
                <Line type="monotone" dataKey="qualified" stroke="#10B981" strokeWidth={2} name="Qualified" />
                <Line type="monotone" dataKey="appointments" stroke="#8B5CF6" strokeWidth={2} name="Appointments" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Source Breakdown & Calls Summary */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Source Breakdown */}
          {sourceData.length > 0 && (
            <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Leads by Source</h2>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {sourceData.map((source, index) => (
                  <div key={source.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-blue-100">{source.name}</span>
                    </div>
                    <span className="text-white font-semibold">{source.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Calls Summary */}
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Call Activity</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="h-6 w-6 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">Total Calls</p>
                    <p className="text-blue-300 text-sm">All incoming calls</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">{formatNumber(summary?.total_calls)}</p>
              </div>

              <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                  <div>
                    <p className="text-white font-medium">Answered</p>
                    <p className="text-green-300 text-sm">Successfully connected</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatNumber(summary?.answered_calls)}</p>
              </div>

              <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <div className="flex items-center gap-3">
                  <PhoneMissed className="h-6 w-6 text-red-400" />
                  <div>
                    <p className="text-white font-medium">Missed</p>
                    <p className="text-red-300 text-sm">Opportunities to follow up</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-red-400">{formatNumber(summary?.missed_calls)}</p>
              </div>

              {summary?.avg_call_duration && (
                <div className="flex items-center justify-between p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <div className="flex items-center gap-3">
                    <Clock className="h-6 w-6 text-purple-400" />
                    <div>
                      <p className="text-white font-medium">Avg Duration</p>
                      <p className="text-purple-300 text-sm">Per call</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">
                    {Math.round(summary.avg_call_duration / 60)}m
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Performance Log */}
        {summary?.leads_timeline && summary.leads_timeline.length > 0 && (
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-semibold text-white">Recent Activity</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Date</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-blue-200 uppercase">Source</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-blue-200 uppercase">Leads</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-blue-200 uppercase">Qualified</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-blue-200 uppercase">Appointments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {summary.leads_timeline.slice(0, 10).map((record, index) => (
                    <tr key={index} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 text-white">{formatDateEST(record.date)}</td>
                      <td className="px-6 py-4 text-blue-200">{record.source}</td>
                      <td className="px-6 py-4 text-right text-white font-semibold">{record.total_leads}</td>
                      <td className="px-6 py-4 text-right text-green-400">{record.qualified_leads}</td>
                      <td className="px-6 py-4 text-right text-purple-400">{record.booked_appointments}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!summary?.total_leads || summary.total_leads === 0) && (
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-12 border border-white/20 text-center">
            <BarChart3 className="w-16 h-16 text-blue-300 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold text-white mb-2">No Performance Data Yet</h3>
            <p className="text-blue-200">
              Your performance metrics will appear here as data is tracked.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
